import React, { useRef, useEffect, useState, useContext } from 'react'
import { motion } from 'framer-motion/dist/framer-motion'
import parse, { domToReact } from 'html-react-parser'

import { UIContext } from '../../../Context/UIContext'

import './WhyUs.scss'

import Section from '../../Section'
import SubSection from './SubSection'

const options = {
    replace: ({ attribs, children }) => {
        if (!attribs) return;

        const tagNames = ['h1', 'h2', 'h3', 'p', 'ul'];
        const tagName = attribs.tagName;

        if (tagNames.includes(tagName)) {
            if (tagName === 'ul') {
                return (
                    <ul>
                        {children.map((child, i) => 
                            <li key={`sub-item-${i}`}>
                                {domToReact(child.children, options)}
                            </li>
                        )}
                    </ul>
                );
            } else {
                return React.createElement(tagName, {}, domToReact(children, options));
            }
        }
    }
}

const drawWave = (color) => {
    return {
        hidden: () => { 
            return {
                pathLength: 0,
                fill: '#FFFFFF',
                fillOpacity: 0,
                stroke: '#FFFFFF',
                opacity: 0
            }
        },
        visible: (i) => {
            let stroke = color
            const strokeDelay = i * 0.5
            const fillDelay = strokeDelay + 0.5
            const transition = {
                pathLength: { delay: strokeDelay, duration: 2, ease: "easeInOut" },
                opacity: { delay: strokeDelay, duration: 0.1 },
                fillOpacity: { delay: fillDelay, duration: 1.5, ease: "easeInOut"},
                fill: { delay: fillDelay, duration: 1.5, ease: "easeInOut"},
            }
            return {
                pathLength: 1.1,
                fill: color,
                fillOpacity: 1,
                stroke,
                opacity: 1,
                pathOffset: 0,
                transition,
            }
        },
    }
}

const WhyUs = ({ data }) => {
    const { title, main, items } = data.content
    const [ svgHeight, setSvgHeight ] = useState(0)
    const { isMobile } = useContext(UIContext)
    const svgRef = useRef(null)

    useEffect(() => {
        if (svgRef.current) {
            setSvgHeight(svgRef.current.clientHeight)
        }
    }, [svgRef])

    return (
        <Section
            name="mga-why-us"
            className="mga-why-section"
            motionProps={{
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                exit: { opacity: 0, transition: { duration: 0.5, when: 'afterChildren' }},
                transition: { duration: 0.1 }
            }}
        >
            <motion.div
                className="mga-why-section__top"
                style={{
                    paddingTop: isMobile ? svgHeight * 1.5 : svgHeight * 1.75,
                }}
            >
                { isMobile ?
                    <motion.svg
                        viewBox="0 0 600 1600"
                        xmlns="http://www.w3.org/2000/svg"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.1 }}
                        style={{ 
                            fillRule: "evenodd", 
                            clipRule: "evenodd", 
                            strokeLinejoin: "round", 
                            strokeMiterlimit: 2,
                            strokeWidth: 0.2
                        }}
                    >
                        <g transform="matrix(.43929 0 0 1 -.066 -68)">
                            <clipPath id="wave1"><path d="M0 68h1366v1599.01H0z"/></clipPath>
                            <g clipPath="url(#wave1)">
                                <motion.path
                                    d="M-2840.97 886.892c-560.97-401.74 1061.1-628.102 3041.84-764.346 1980.74-136.244 3830.27-14.929 4131.05 270.962 0 0-3238.31 2877.902-7172.89 493.384z"
                                    transform="translate(0 -2)"
                                    stroke="#E23B00"
                                    fill="#FFFFFF"
                                    variants={drawWave("#E23B00")}
                                    initial="hidden"
                                    animate="visible"
                                    custom={0}
                                    style={{
                                        fillRule: "nonzero",
                                    }}
                                />
                            </g>
                        </g>
                    </motion.svg>
                :
                    <motion.svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox='0 0 1366 1600'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.1 }}
                        style={{ 
                            fillRule: "evenodd", 
                            clipRule: "evenodd", 
                            strokeLinejoin: "round", 
                            strokeMiterlimit: 2,
                            strokeWidth: 0.2
                        }}
                    >
                        <g transform="translate(0 -68)">
                            <clipPath id="wave1"><path d="M0 68h1366v1599.01H0z"/></clipPath>
                            <g clipPath="url(#wave1)">
                                <motion.path
                                    d="M-2840.97 886.892c-560.97-401.74 1061.1-628.102 3041.84-764.346 1980.74-136.244 3830.27-14.929 4131.05 270.962 0 0-3238.31 2877.902-7172.89 493.384z"
                                    stroke="#E23B00"
                                    fill="#FFFFFF"
                                    variants={drawWave("#E23B00")}
                                    initial="hidden"
                                    animate="visible"
                                    custom={0}
                                    style={{
                                        fillRule: "nonzero",
                                    }}
                                />
                            </g>
                        </g>
                    </motion.svg>
                }
                <motion.div className="mga-section__wrapper">
                    <motion.h1
                        initial={{ opacity: 0, x: '-30%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '-30%', transition: { delay: 0 } }}
                        transition={{ duration: 0.75, delay: 1 }}
                    >
                        { title }
                    </motion.h1>
                    <motion.div
                        className="mga-why-section__top__info"
                        initial={{ opacity: 0, x: '-30%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '-30%', transition: { delay: 0 } }}
                        transition={{ duration: 1, delay: 1 }}
                    >
                        {parse(main, options)}
                    </motion.div>
                </motion.div>
            </motion.div>
            <motion.div
                className="mga-why-section__bottom"
                initial={{ y: '20%'}}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 }}
                style={{
                    marginTop: svgHeight * 1.25,
                }}
            >
                { isMobile ?
                    <svg
                        ref={svgRef}
                        viewBox="0 0 600 128"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <g transform="matrix(.43929 0 0 1 -.066 -68)">
                            <clipPath id="wave2"><path d="M0 68h1366v128.01H0z"/></clipPath>
                            <g clipPath="url(#wave2)">
                                <path d="M-2793.49 1135.7C-3354.47 733.96-1747.6 366.656 200.87 159.217c2136.35-227.441 3830.27-51.6 4131.05 234.291 0 0-3190.83 3126.712-7125.41 742.192z" transform="translate(0 -2)"/>
                            </g>
                        </g>
                    </svg>
                :
                    <svg
                        ref={svgRef}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox='0 0 1366 73'
                    >
                        <g transform="translate(0 -64)">
                            <clipPath id="wave2"><path d="M0 65h1366v72H0z"/></clipPath>
                            <g clipPath="url(#wave2)">
                                <path d="M-2840.97 886.892c-560.97-401.74 1061.1-628.102 3041.84-764.346 1980.74-136.244 3830.27-14.929 4131.05 270.962 0 0-5718.82 1534.722-7172.89 493.384z" transform="translate(0 -6)"/>
                            </g>
                        </g>
                    </svg>
                }
                <div className="mga-section__wrapper">
                    {items.map((item, index) => <SubSection data={item} key={`why-us-item-${index}`} />)}
                </div>
            </motion.div>
        </Section>
    )
}

export default React.memo(WhyUs)
